import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { getErrorMessage } from "@/lib/errors";

interface PlanRow {
  plan_key: string;
  plan_name: string;
  billing_interval: string;
  households: number;
  active_households: number;
  max_teens: number;
  monthly_revenue: number;
}

interface FixedCost {
  vendor: string;
  description: string | null;
  monthly_usd: number;
  cost_class: string;
}

interface ProviderRow {
  provider_key: string;
  capability: string;
  model_id: string | null;
  enabled: boolean;
  estimated_cost_usd: number | null;
}

interface UnitEconomics {
  window_days: number;
  generated_at: string;
  qa_excluded_subscription_rows: number;
  plans: PlanRow[];
  households: number;
  active_learners: number;
  family_occupancy: Record<string, number>;
  revenue: { gross_monthly_usd: number };
  costs: {
    payment_cogs_usd: number;
    per_learner_runtime_cogs_usd: number;
    shared_generation_usd: number;
    fixed_platform_overhead_usd: number;
    non_cogs_business_expenses_usd: number;
  };
  contribution_usd: number;
  contribution_margin_pct: number | null;
  operating_margin_pct: number | null;
  cost_per_household_usd: number | null;
  cost_per_learner_usd: number | null;
  break_even_individual_subscribers: number | null;
  ceilings: Record<string, number>;
  fixed_costs: FixedCost[];
  providers: ProviderRow[];
}

const WINDOWS = [7, 30, 90, 365];

const money = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `$${Number(n).toFixed(2)}`;

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export default function AdminUnitEconomics() {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState<UnitEconomics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: e } = await supabase.rpc("get_unit_economics", {
        p_window_days: windowDays,
      });
      if (e) throw e;
      setData(res as unknown as UnitEconomics);
    } catch (e: unknown) {
      setError(getErrorMessage(e) ?? "Failed to load unit economics");
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    void run();
  }, [run]);

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin">
                <ArrowLeft className="mr-1 h-4 w-4" /> Admin
              </Link>
            </Button>
            <h1 className="font-heading text-2xl font-bold text-foreground">Unit Economics</h1>
          </div>
          <div className="flex items-center gap-2">
            {WINDOWS.map((w) => (
              <Button
                key={w}
                size="sm"
                variant={w === windowDays ? "default" : "outline"}
                onClick={() => setWindowDays(w)}
              >
                {w}d
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => void run()} disabled={loading}>
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Production figures only. QA, test and billing-exempt households are excluded from every
          revenue, population and margin number on this page.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-6 text-destructive">{error}</CardContent>
          </Card>
        ) : data ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Gross monthly revenue" value={money(data.revenue.gross_monthly_usd)} />
              <Stat label="Contribution" value={money(data.contribution_usd)} hint={
                data.contribution_margin_pct === null ? "No paid revenue yet" : `${data.contribution_margin_pct}% margin`
              } />
              <Stat label="Paying households" value={String(data.households)} hint={`${data.active_learners} active learners`} />
              <Stat
                label="Operating margin"
                value={data.operating_margin_pct === null ? "—" : `${data.operating_margin_pct}%`}
                hint="After shared generation and fixed overhead"
              />
            </div>

            <Card>
              <CardHeader><CardTitle>Cost breakdown</CardTitle></CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 text-sm">
                <Stat label="A. Payment COGS" value={money(data.costs.payment_cogs_usd)} />
                <Stat label="B. Per-learner runtime" value={money(data.costs.per_learner_runtime_cogs_usd)} />
                <Stat label="C. Shared generation" value={money(data.costs.shared_generation_usd)} hint="Amortized across all exposures" />
                <Stat label="D. Fixed overhead" value={money(data.costs.fixed_platform_overhead_usd)} />
                <Stat label="E. Business expenses" value={money(data.costs.non_cogs_business_expenses_usd)} hint="Not COGS" />
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle>Per-unit costs and ceilings</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Cost per household</span><span>{money(data.cost_per_household_usd)}</span></div>
                  <div className="flex justify-between"><span>Cost per learner</span><span>{money(data.cost_per_learner_usd)}</span></div>
                  <div className="flex justify-between"><span>Break-even Individual subscribers</span><span>{data.break_even_individual_subscribers ?? "—"}</span></div>
                  {Object.entries(data.ceilings).map(([plan, ceiling]) => (
                    <div key={plan} className="flex justify-between text-muted-foreground">
                      <span>Ceiling · {plan}</span><span>{money(ceiling)} / month</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Family occupancy</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {Object.keys(data.family_occupancy).length === 0 ? (
                    <p className="text-muted-foreground">No paying households yet.</p>
                  ) : (
                    Object.entries(data.family_occupancy)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([seats, n]) => (
                        <div key={seats} className="flex justify-between">
                          <span>{seats} learner{seats === "1" ? "" : "s"}</span>
                          <span>{n} household{n === 1 ? "" : "s"}</span>
                        </div>
                      ))
                  )}
                  <p className="pt-2 text-xs text-muted-foreground">
                    {data.qa_excluded_subscription_rows} QA / billing-exempt row(s) excluded.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle>Plans in production</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.plans.length === 0 ? (
                  <p className="text-muted-foreground">No production subscriptions recorded.</p>
                ) : (
                  data.plans.map((p) => (
                    <div key={`${p.plan_key}-${p.billing_interval}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-2">
                      <div>
                        <p className="font-medium text-foreground">{p.plan_name}</p>
                        <p className="text-xs text-muted-foreground">{p.plan_key} · {p.billing_interval} · max {p.max_teens} teen(s)</p>
                      </div>
                      <div className="text-right">
                        <p>{money(p.monthly_revenue)} / month</p>
                        <p className="text-xs text-muted-foreground">{p.active_households}/{p.households} active</p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Fixed stack and metered providers</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="space-y-1">
                  {data.fixed_costs.map((f) => (
                    <div key={f.vendor} className="flex justify-between">
                      <span>{f.vendor} <span className="text-xs text-muted-foreground">{f.description}</span></span>
                      <span>{money(f.monthly_usd)} · {f.cost_class === "business_expense" ? "non-COGS" : "overhead"}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {data.providers.map((p) => (
                    <Badge key={`${p.provider_key}-${p.capability}`} variant={p.enabled ? "default" : "outline"}>
                      {p.provider_key} · {p.capability}{p.model_id ? ` · ${p.model_id}` : ""}
                      {p.estimated_cost_usd ? ` · ${money(p.estimated_cost_usd)}/call` : ""}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
              Generated {new Date(data.generated_at).toLocaleString()} · window {data.window_days} days.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
